import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Select } from '@/components/ui/Select';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { useMyPreferences, useSaveMyPreferences, type UserPreferences } from '@/hooks/useSettings';

const DEFAULT: UserPreferences = {
  density: 'comfortable',
  emailDigest: 'daily',
  notifyOnOrders: true,
  notifyOnLowStock: true,
};

export function PreferencesSettings() {
  const { data, isLoading } = useMyPreferences();
  const save = useSaveMyPreferences();
  const [prefs, setPrefs] = useState<UserPreferences>(DEFAULT);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) { setPrefs({ ...DEFAULT, ...data }); setDirty(false); }
  }, [data]);

  function update<K extends keyof UserPreferences>(key: K, val: UserPreferences[K]) {
    setPrefs((p) => ({ ...p, [key]: val }));
    setDirty(true);
  }

  async function onSave() {
    try {
      await save.mutateAsync(prefs);
      toast.success('Preferences saved');
      setDirty(false);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Failed to save preferences.');
    }
  }

  if (isLoading) return <LoaderScreen />;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="kicker mb-2">Preferences · Personal</div>
          <h2 className="font-display text-2xl text-text">Your Settings</h2>
          <p className="text-sm text-text-muted mt-2 max-w-xl">
            These settings only affect your account. Other team members can configure their own.
          </p>
        </div>
        <Button onClick={onSave} leftIcon={<Save size={14} />} loading={save.isPending} disabled={!dirty}>
          Save
        </Button>
      </div>

      {/* Display */}
      <Card>
        <header className="mb-6 pb-4 border-b border-border">
          <div className="kicker mb-1.5">Display</div>
          <h3 className="font-display text-lg text-text">Layout & density</h3>
        </header>
        <div className="max-w-sm">
          <Select
            label="Table density"
            value={prefs.density}
            onChange={(e) => update('density', e.target.value as UserPreferences['density'])}
            options={[
              { value: 'comfortable', label: 'Comfortable — more room' },
              { value: 'compact',     label: 'Compact — more rows visible' },
            ]}
            hint="Affects table rows in clients, orders, inventory, etc."
          />
        </div>
      </Card>

      {/* Notifications */}
      <Card>
        <header className="mb-6 pb-4 border-b border-border">
          <div className="kicker mb-1.5">Notifications</div>
          <h3 className="font-display text-lg text-text">What you get notified about</h3>
        </header>

        <div className="max-w-md mb-6">
          <Select
            label="Email digest"
            value={prefs.emailDigest}
            onChange={(e) => update('emailDigest', e.target.value as UserPreferences['emailDigest'])}
            options={[
              { value: 'off',    label: 'Off' },
              { value: 'daily',  label: 'Daily — every morning' },
              { value: 'weekly', label: 'Weekly — Monday morning' },
            ]}
            hint="Summary of activity in modules you have access to."
          />
        </div>

        <div className="space-y-4 pt-5 border-t border-border">
          <Switch
            checked={prefs.notifyOnOrders}
            onChange={(v) => update('notifyOnOrders', v)}
            label="New order alerts"
            description="Notify me when a new sales order is created."
          />
          <Switch
            checked={prefs.notifyOnLowStock}
            onChange={(v) => update('notifyOnLowStock', v)}
            label="Low stock alerts"
            description="Notify me when an inventory item drops below its reorder point."
          />
        </div>
      </Card>

      {/* Sticky save bar */}
      {dirty && (
        <div className="sticky bottom-4 z-10 flex items-center justify-between p-4 bg-bg-raised border border-brass-400/40 rounded-sm shadow-raised animate-slide-up">
          <span className="text-sm text-text">Unsaved preference changes.</span>
          <Button onClick={onSave} leftIcon={<Save size={14} />} loading={save.isPending}>
            Save
          </Button>
        </div>
      )}
    </div>
  );
}
